import { Button } from '@ideoz/ui';
import { Check, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ScopeSuggestionItem } from './useScopeSuggestions';

interface ScopeSuggestionCardProps {
  suggestion: ScopeSuggestionItem;
  index: number;
  onAdd: (id: string) => void;
  onIgnore: (id: string) => void;
}

/** Dumb: one suggested scope with Ignore / Add (mirrors the All Scopes card style). */
export function ScopeSuggestionCard({
  suggestion,
  index,
  onAdd,
  onIgnore,
}: ScopeSuggestionCardProps): JSX.Element {
  const { t } = useTranslation();
  const { name, description, status } = suggestion;
  const resolved = status === 'added' || status === 'ignored';

  return (
    <div
      className={`flex items-start justify-between gap-4 rounded-xl border px-4 py-3 transition-colors ${
        status === 'ignored'
          ? 'border-gray-100 bg-gray-50 opacity-60'
          : 'border-gray-200 bg-background'
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-gray-400">
          {t('suggestScopes.scopeLabel', { n: index + 1 })}
        </p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{name}</p>
        {description && (
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</p>
        )}
      </div>

      {status === 'added' ? (
        <span className="flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-emerald-600">
          <Check className="h-4 w-4" /> {t('suggestScopes.added')}
        </span>
      ) : status === 'ignored' ? (
        <span className="whitespace-nowrap text-sm text-gray-400">{t('suggestScopes.ignored')}</span>
      ) : (
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="outline" onClick={() => onIgnore(suggestion.id)} disabled={resolved}>
            {t('suggestScopes.ignore')}
          </Button>
          <Button
            onClick={() => onAdd(suggestion.id)}
            disabled={status === 'adding'}
            className="gap-1.5"
          >
            {status === 'adding' && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('suggestScopes.add')}
          </Button>
        </div>
      )}
    </div>
  );
}
